import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HeartHandshake, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pollConfirm, readPending, clearPending } from "@/lib/checkout";

export default function ThanksPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const result = params.get("result");
  const donationId = params.get("donation_id") || readPending();
  const [status, setStatus] = useState(result === "cancel" ? "cancelled" : "confirming");

  useEffect(() => {
    let active = true;
    (async () => {
      if (result === "cancel") { clearPending(); return; }
      if (!donationId) { setStatus("unknown"); return; }
      const final = await pollConfirm(donationId);
      clearPending();
      if (!active) return;
      setStatus(final === "sent" || final === "fee_paid" ? "done" : "pending");
    })();
    return () => { active = false; };
  }, [donationId, result]);

  const views = {
    confirming: {
      icon: <Loader2 className="animate-spin" size={40} />,
      bg: "#1E40AF",
      title: "Confirming your blessing…",
      body: "Hang tight — we're finalizing your gift with Stripe.",
    },
    done: {
      icon: <CheckCircle2 size={40} />,
      bg: "#22C55E",
      title: "Blessing sent! 💜",
      body: "Your gift is on its way — it'll arrive in their bank in ~2 business days. Thank you for making someone's day.",
    },
    pending: {
      icon: <Clock size={40} />,
      bg: "#FF8A3D",
      title: "Payment received — almost there",
      body: "Your payment went through and is settling. It'll appear in the live feed shortly.",
    },
    cancelled: {
      icon: <XCircle size={40} />,
      bg: "#64748B",
      title: "Checkout cancelled",
      body: "No charge was made. You can pick someone to bless whenever you're ready.",
    },
    unknown: {
      icon: <HeartHandshake size={40} />,
      bg: "#1E40AF",
      title: "Thanks for your kindness",
      body: "If you completed a payment, it'll show up in the live feed shortly.",
    },
  };
  const v = views[status] || views.unknown;

  return (
    <div className="max-w-lg mx-auto px-4 py-16 grid place-items-center text-center" data-testid="thanks-page">
      <div className="w-20 h-20 rounded-pill grid place-items-center text-white mb-6 animate-pop-in" style={{ background: v.bg }}>
        {v.icon}
      </div>
      <h1 className="text-3xl font-black" data-testid={`thanks-status-${status}`}>{v.title}</h1>
      <p className="text-base text-ink-tertiary font-semibold mt-3 max-w-sm leading-relaxed">{v.body}</p>
      <div className="flex flex-col sm:flex-row gap-3 mt-8 w-full max-w-sm">
        <Button data-testid="thanks-view-feed" onClick={() => navigate("/live")}
          className="flex-1 h-12 rounded-pill font-black bg-brand-primary text-white hover:opacity-90">
          See the live feed
        </Button>
        <Button data-testid="thanks-go-map" variant="outline" onClick={() => navigate("/")}
          className="flex-1 h-12 rounded-pill font-black border-border">
          Back to map
        </Button>
      </div>
    </div>
  );
}
